import type { Member } from '@/features/iam/members/Member'
import type {
  GetCurrentUserWorkspaceMembershipsFn,
  PrimeMemberFn,
} from '@/features/iam'
import type { User } from '@/features/iam/users/User'
import type { FetchJobFn, JobFn } from '@/features/jobs'
import type { NotificationPreferences } from '@/features/notifications/notification-preferences'
import type { NavigateFn } from '@/RootStore'
import type {
  AlertErrorHandlerFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { createInternalFeatureLoader } from '@/utils/feature-loading-util'
import { toStream } from '@/utils/mobx-util'
import { type Observable, switchMap, take } from 'rxjs'

/**
 * Used for showing desktop notifications.
 */
export interface SystemNotification {
  title: string
  body: string
  tag?: string
  icon?: string
  timeout?: number
  close$?: Observable<unknown>
  onClick?(): void
}

/**
 * Shows a notification through the operating system.
 */
export type ShowDesktopNotificationFn = (opts: SystemNotification) => void

/**
 * Whether desktop notifications are enabled, and whether the browser denied
 * permission for them.
 */
export interface DesktopNotificationState {
  enabled: boolean
  denied: boolean
}

/**
 * Enables or disables desktop notifications, asking the browser for
 * permission when enabling.
 */
export type SetDesktopNotificationsEnabledFn = (
  enabled: boolean,
) => Promise<void>

/**
 * Fetches a member's notification preferences.
 */
export type FetchNotificationPreferencesFn = (
  member: Member,
  signal?: AbortSignal,
) => Promise<NotificationPreferences>

/**
 * Saves a member's notification preferences.
 */
export type SaveNotificationPreferencesFn = (
  member: Member,
  preferences: NotificationPreferences,
) => Promise<void>

/**
 * Gets the current time.
 */
export type GetTimeFn = () => Date

/**
 * Shows or hides the notifications sidebar.
 */
export type ToggleNotificationsSidebarFn = () => void

/**
 * Options for {@link NotificationsFeature}.
 */
export interface NotificationsFeatureOptions {
  /**
   * Stream of the API token.
   */
  token$: Observable<string>

  /**
   * Delegate for navigating with the client-side router.
   */
  navigate: NavigateFn

  /**
   * Gets the current time.
   */
  getTime: GetTimeFn

  /**
   * Creates a handler that shows a failed write in an alert.
   */
  createAlertErrorHandler: AlertErrorHandlerFactory

  /**
   * Wraps a read so that a failure shows an alert offering a retry.
   */
  createAlertRetryWrapper: AlertRetryWrapperFactory

  /**
   * Stream of the signed-in user.
   */
  currentUser$: Observable<User>

  /**
   * Stream of the current member.
   */
  currentMember$: Observable<Member>

  /**
   * Delegate for priming members referenced by notifications.
   */
  primeMember: PrimeMemberFn

  /**
   * Delegate for getting the user's workspace memberships.
   */
  getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn

  /**
   * Reactive getter for a loaded job.
   */
  job: JobFn

  /**
   * Delegate for fetching a job.
   */
  fetchJob: FetchJobFn

  /**
   * Desktop notifications raised by other features.
   */
  systemNotifications$: Observable<SystemNotification>
}

/**
 * Notifications, desktop notifications and notification preferences.
 */
export type NotificationsFeature = ReturnType<typeof NotificationsFeature>

export function NotificationsFeature(options: NotificationsFeatureOptions) {
  const internal = createInternalFeatureLoader(
    () => import('./internal'),
    (m) => m.InternalNotificationsFeature(options),
  )

  const desktopNotificationState$: Observable<DesktopNotificationState> =
    internal.deferUntilLoaded((m) =>
      toStream(
        () => ({
          enabled: m.desktopNotificationStore.isEnabled,
          denied: m.desktopNotificationStore.isDenied,
        }),
        true,
      ),
    )

  const setDesktopNotificationsEnabled: SetDesktopNotificationsEnabledFn =
    internal.wrap(
      (m) => (enabled: boolean) =>
        m.desktopNotificationStore.setEnabled(enabled),
    )

  const fetchNotificationPreferences: FetchNotificationPreferencesFn =
    internal.wrap(
      (m) => (member: Member, signal?: AbortSignal) =>
        m.notificationPreferencesStore.fetchPreferences(member, signal),
    )

  const saveNotificationPreferences: SaveNotificationPreferencesFn =
    internal.wrap(
      (m) => (member: Member, preferences: NotificationPreferences) =>
        m.notificationPreferencesStore.savePreferences(member, preferences),
    )

  options.token$.pipe(take(1), switchMap(internal)).subscribe()

  return {
    // Internal
    internal,

    // Public
    desktopNotificationState$,
    setDesktopNotificationsEnabled,
    fetchNotificationPreferences,
    saveNotificationPreferences,
    toggleNotificationsSidebar: internal.wrap(
      (m) => () => m.notificationsSidebarVm.toggle(),
    ) satisfies ToggleNotificationsSidebarFn,
    unreadCount$: internal.defer((m) => m.unreadCount$),
  }
}
